import { Link } from "react-router-dom";
import { Briefcase, Mail, Linkedin, ArrowUpRight, MapPin } from "lucide-react";
import servicesBg from "@/assets/services-bg-tiny.webp";
import { NewsletterSubscribe } from "@/components/NewsletterSubscribe";

const quickLinks = [
  { name: "Home", href: "/" },
  { name: "About", href: "/about" },
  { name: "Services", href: "/services" },
  { name: "Portfolio", href: "/portfolio" },
  { name: "Blog", href: "/blog" },
  { name: "Contact", href: "/contact" },
];

const services = [
  { name: "Admin Support", href: "/services" },
  { name: "Business Documentation", href: "/services" },
  { name: "Market Research", href: "/services" },
];

const legal = [
  { name: "Terms & Conditions", href: "/terms" },
  { name: "Privacy Policy", href: "/privacy" },
  { name: "Disclaimer", href: "/disclaimer" },
  { name: "FAQ", href: "/faq" },
];

export function Footer() {
  return (
    <footer className="bg-primary text-primary-foreground relative overflow-hidden">
      {/* Background image with overlay */}
      <div
        className="absolute inset-0 opacity-5"
        style={{
          backgroundImage: `url(${servicesBg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      
      {/* Decorative elements */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-accent/5 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-72 h-72 bg-accent/5 rounded-full blur-3xl" />
      
      <div className="container mx-auto px-4 lg:px-8 py-16 relative">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12">
          {/* Brand */}
          <div className="lg:col-span-2">
            <Link to="/" className="flex items-center gap-2 mb-4">
              <div className="p-2 rounded-lg bg-gradient-sky shadow-glow">
                <Briefcase className="h-5 w-5 text-accent-foreground" />
              </div>
              <div className="flex flex-col">
                <span className="text-xl font-bold">
                  Vantage<span className="text-accent">Strategic</span>
                </span>
                <span className="text-sm text-primary-foreground/70">by Hervé K</span>
              </div>
            </Link>
            <p className="text-primary-foreground/70 text-sm leading-relaxed mb-4">
              Business administration support that helps you execute with clarity and professionalism.
            </p>
            <div className="flex items-center gap-2 text-primary-foreground/60 text-sm mb-4">
              <MapPin className="h-4 w-4 text-accent" />
              Chattanooga, Tennessee, USA
            </div>
            <p className="text-primary-foreground/50 text-xs leading-relaxed">
              International student at an accredited institution. 
              Services provided as part of educational development and professional portfolio building.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-semibold mb-4 text-accent text-base">Quick Links</h3>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-sm text-primary-foreground/70 hover:text-accent transition-colors inline-flex items-center gap-1 group"
                  >
                    {link.name}
                    <ArrowUpRight className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services & Newsletter */}
          <div>
            <h3 className="font-semibold mb-4 text-accent text-base">Services</h3>
            <ul className="space-y-3 mb-6">
              {services.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-sm text-primary-foreground/70 hover:text-accent transition-colors inline-flex items-center gap-1 group"
                  >
                    {link.name}
                    <ArrowUpRight className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </Link>
                </li>
              ))}
            </ul>
            
            {/* Community Newsletter */}
            <div className="mt-4">
              <h4 className="text-sm font-medium text-primary-foreground/80 mb-2">Join Our Community</h4>
              <NewsletterSubscribe />
            </div>
          </div>

          {/* Legal & Contact */}
          <div>
            <h3 className="font-semibold mb-4 text-accent text-base">Legal</h3>
            <ul className="space-y-3 mb-6">
              {legal.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-sm text-primary-foreground/70 hover:text-accent transition-colors inline-flex items-center gap-1 group"
                  >
                    {link.name}
                    <ArrowUpRight className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </Link>
                </li>
              ))}
            </ul>
            
            <h3 className="font-semibold mb-4 text-accent text-base">Contact</h3>
            <ul className="space-y-3">
              <li>
                <a
                  href="mailto:herve@VantageStrategic.co"
                  className="text-sm text-primary-foreground/70 hover:text-accent transition-colors inline-flex items-center gap-2"
                >
                  <Mail className="h-4 w-4" />
                  herve@VantageStrategic.co
                </a>
              </li>
              <li>
                <a
                  href="https://www.linkedin.com/in/herve-nkurikiyimfura-7b0720252/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-primary-foreground/70 hover:text-accent transition-colors inline-flex items-center gap-2"
                >
                  <Linkedin className="h-4 w-4" />
                  LinkedIn Profile
                </a>
              </li>
            </ul>
          </div>
        </div>


        <div className="mt-12 pt-8 border-t border-primary-foreground/10 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-sm text-primary-foreground/50">
            © {new Date().getFullYear()} VantageStrategic by Hervé K. All rights reserved.
          </p>
          <p className="text-sm text-primary-foreground/50 text-center">
            Built with professionalism and care by Hervé K in Chattanooga, Tennessee.
          </p>
        </div>
      </div>
    </footer>
  );
}
